import { Logger } from '../../services/Logger';
import { AppState } from './AppState';

const transitions: Readonly<Record<AppState, readonly AppState[]>> = {
  [AppState.Booting]: [AppState.Loading],
  [AppState.Loading]: [AppState.Intro, AppState.Ready],
  [AppState.Intro]: [AppState.Ready],
  [AppState.Ready]: [AppState.Playing, AppState.Intro],
  [AppState.Playing]: [AppState.Hit, AppState.Paused, AppState.GameOver],
  [AppState.Hit]: [AppState.Playing, AppState.GameOver],
  [AppState.Paused]: [AppState.Playing],
  [AppState.GameOver]: [AppState.Restarting, AppState.Intro],
  [AppState.Restarting]: [AppState.Ready],
};

export type StateListener = (from: AppState, to: AppState) => void;

export class GameStateMachine {
  private listeners = new Set<StateListener>();

  public constructor(
    private current: AppState,
    private readonly logger: Logger,
  ) {}

  public get state(): AppState {
    return this.current;
  }

  public canTransition(to: AppState): boolean {
    return transitions[this.current].includes(to);
  }

  public transition(to: AppState): boolean {
    if (!this.canTransition(to)) {
      this.logger.warn(`Rejected state transition ${this.current} -> ${to}`);
      return false;
    }
    const from = this.current;
    this.current = to;
    this.listeners.forEach((listener) => listener(from, to));
    return true;
  }

  public subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
